import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const resolveDefaultApiBase = () => {
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        return 'http://localhost:8080';
    }
    return '';
};

export const API_BASE = process.env.REACT_APP_API_BASE_URL || resolveDefaultApiBase();

export const api = axios.create({
    baseURL: API_BASE,
    timeout: 15000,
});

// ── LUXVONO Two-Tier Stale-While-Revalidate Web Cache (L1 RAM + L2 sessionStorage) ──
export const LuxWebCache = (() => {
    const L1 = new Map();
    const L1_MAX = 120;
    const STORAGE_PREFIX = 'lux_swr_v1:';
    const SHORT_TTL = 2 * 60 * 1000;   // 2 min (feeds/videos)
    const DEFAULT_TTL = 5 * 60 * 1000; // 5 min (products/stores/discovery)
    const LONG_TTL = 30 * 60 * 1000;   // 30 min (categories/banners/settings)

    const CACHEABLE_PATTERNS = [
        '/api/v1/products',
        '/api/v1/categories',
        '/api/v1/banners',
        '/api/v1/discovery',
        '/api/v1/settings/public',
        '/api/v1/videos',
        '/api/v1/feed',
        '/api/v1/stores'
    ];

    function getLocaleSuffix() {
        try {
            if (typeof window === 'undefined') return '_ALL_DEF_ar';
            let country = localStorage.getItem('lux_country') || '';
            if (!country) {
                const geoStr = localStorage.getItem('luxvono_geo_context');
                if (geoStr) {
                    const parsed = JSON.parse(geoStr);
                    if (parsed?.country) country = parsed.country;
                }
            }
            const currency = localStorage.getItem('luxvono_user_currency') || 'DEF';
            const lang = localStorage.getItem('luxvono_user_explicit_language') || 'ar';
            return `_${String(country || 'ALL').toUpperCase()}_${String(currency).toUpperCase()}_${String(lang).toLowerCase()}`;
        } catch (_) {
            return '_ALL_DEF_ar';
        }
    }

    function isCacheableUrl(url) {
        const clean = String(url || '').toLowerCase();
        if (clean.includes('/admin/') || clean.includes('/seller/') || clean.includes('/vendor/') || clean.includes('/reviews')) {
            return false;
        }
        return CACHEABLE_PATTERNS.some(p => clean.includes(p));
    }

    function ttlForUrl(url) {
        const clean = String(url || '').toLowerCase();
        if (clean.includes('/categories') || clean.includes('/banners') || clean.includes('/settings/public')) {
            return LONG_TTL;
        }
        if (clean.includes('/feed') || clean.includes('/videos')) {
            return SHORT_TTL;
        }
        return DEFAULT_TTL;
    }

    function buildKey(url, config) {
        let paramsPart = '';
        if (config?.params) {
            try {
                paramsPart = '?' + JSON.stringify(config.params);
            } catch (_) {}
        }
        return `${url}${paramsPart}${getLocaleSuffix()}`;
    }

    function get(key) {
        const now = Date.now();
        const mem = L1.get(key);
        if (mem) {
            const age = now - mem.createdAt;
            if (age <= mem.ttl * 3) {
                return { data: mem.data, status: mem.status, headers: mem.headers, isStale: age > mem.ttl };
            }
            L1.delete(key);
        }
        try {
            if (typeof sessionStorage !== 'undefined') {
                const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
                if (raw) {
                    const env = JSON.parse(raw);
                    const age = now - env.createdAt;
                    if (age <= env.ttl * 3) {
                        L1.set(key, env);
                        return { data: env.data, status: env.status || 200, headers: env.headers || {}, isStale: age > env.ttl };
                    }
                    sessionStorage.removeItem(STORAGE_PREFIX + key);
                }
            }
        } catch (_) {}
        return null;
    }

    function put(key, response, ttl) {
        const entry = {
            data: response.data,
            status: response.status || 200,
            headers: response.headers || {},
            createdAt: Date.now(),
            ttl: ttl || DEFAULT_TTL
        };
        if (L1.size >= L1_MAX) {
            const oldestKey = L1.keys().next().value;
            if (oldestKey) L1.delete(oldestKey);
        }
        L1.set(key, entry);
        try {
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
            }
        } catch (_) {}
    }

    function clear() {
        L1.clear();
        try {
            if (typeof sessionStorage !== 'undefined') {
                const keysToRemove = [];
                for (let i = 0; i < sessionStorage.length; i++) {
                    const k = sessionStorage.key(i);
                    if (k && k.startsWith(STORAGE_PREFIX)) {
                        keysToRemove.push(k);
                    }
                }
                keysToRemove.forEach(k => sessionStorage.removeItem(k));
            }
        } catch (_) {}
    }

    const decodedImages = new Set();

    function markImageDecoded(url) {
        if (!url) return;
        decodedImages.add(String(url));
    }

    function isImageDecoded(url) {
        if (!url) return false;
        return decodedImages.has(String(url));
    }

    function preloadImages(urls = []) {
        if (typeof window === 'undefined' || !Array.isArray(urls)) return;
        urls.forEach(raw => {
            if (!raw) return;
            const resolved = typeof resolveStorefrontImageUrl === 'function' ? resolveStorefrontImageUrl(raw) : String(raw);
            if (!resolved || decodedImages.has(resolved)) return;
            try {
                const img = new window.Image();
                img.decoding = 'async';
                img.src = resolved;
                if (img.complete) {
                    decodedImages.add(resolved);
                } else if (typeof img.decode === 'function') {
                    img.decode().then(() => decodedImages.add(resolved)).catch(() => {});
                } else {
                    img.onload = () => decodedImages.add(resolved);
                }
            } catch (_) {}
        });
    }

    function extractArrayPayload(raw) {
        if (!raw) return [];
        const d = raw?.data !== undefined ? raw.data : raw;
        const content = d?.content || d?.data || d;
        return Array.isArray(content) ? content : [];
    }

    function scanEntries(urlSubstring) {
        const matches = [];
        for (const [k, v] of L1.entries()) {
            if (k.includes(urlSubstring) && v?.data) {
                matches.push(v.data);
            }
        }
        if (matches.length === 0 && typeof sessionStorage !== 'undefined') {
            try {
                for (let i = 0; i < sessionStorage.length; i++) {
                    const sk = sessionStorage.key(i);
                    if (sk && sk.startsWith(STORAGE_PREFIX) && sk.includes(urlSubstring)) {
                        const raw = sessionStorage.getItem(sk);
                        if (raw) {
                            const parsed = JSON.parse(raw);
                            if (parsed?.data) {
                                L1.set(sk.slice(STORAGE_PREFIX.length), parsed);
                                matches.push(parsed.data);
                            }
                        }
                    }
                }
            } catch (_) {}
        }
        return matches;
    }

    function getCachedProductList() {
        const payloads = scanEntries('/api/v1/products?');
        for (const p of payloads) {
            const arr = extractArrayPayload(p);
            if (arr.length > 0) return arr;
        }
        return [];
    }

    function getCachedCategoryList() {
        const payloads = scanEntries('/api/v1/categories');
        for (const p of payloads) {
            const arr = extractArrayPayload(p);
            if (arr.length > 0) return arr;
        }
        return [];
    }

    function getCachedVideoList() {
        const payloads = scanEntries('/api/v1/feed');
        for (const p of payloads) {
            const arr = extractArrayPayload(p);
            if (arr.length > 0) return arr;
        }
        const vidPayloads = scanEntries('/api/v1/videos');
        for (const p of vidPayloads) {
            const arr = extractArrayPayload(p);
            if (arr.length > 0) return arr;
        }
        return [];
    }

    function getCachedBannerList() {
        const payloads = scanEntries('/api/v1/banners');
        for (const p of payloads) {
            const arr = extractArrayPayload(p);
            if (arr.length > 0) return arr;
        }
        return [];
    }

    function findCachedProduct(productId) {
        const numId = Number(productId);
        if (!numId) return null;
        const direct = get(buildKey(`/api/v1/products/${numId}`, {}));
        if (direct?.data) {
            const unwrapped = direct.data?.data !== undefined ? direct.data.data : direct.data;
            if (unwrapped && Number(unwrapped.id) === numId) return unwrapped;
        }
        const catalogList = getCachedProductList();
        const found = catalogList.find(item => Number(item?.id) === numId);
        return found || null;
    }

    return {
        isCacheableUrl,
        ttlForUrl,
        buildKey,
        get,
        put,
        clear,
        markImageDecoded,
        isImageDecoded,
        preloadImages,
        getCachedProductList,
        getCachedCategoryList,
        getCachedVideoList,
        getCachedBannerList,
        findCachedProduct
    };
})();

const rawApiGet = api.get.bind(api);
api.get = function (url, config = {}) {
    if (config?._swrBackground || config?.skipCache || !LuxWebCache.isCacheableUrl(url)) {
        return rawApiGet(url, config).then(res => {
            if (LuxWebCache.isCacheableUrl(url)) {
                LuxWebCache.put(LuxWebCache.buildKey(url, config), res, LuxWebCache.ttlForUrl(url));
            }
            return res;
        });
    }

    const cacheKey = LuxWebCache.buildKey(url, config);
    const cached = LuxWebCache.get(cacheKey);

    if (cached) {
        if (cached.isStale) {
            // Stale-While-Revalidate: revalidate silently in background
            rawApiGet(url, { ...config, _swrBackground: true })
                .then(freshRes => LuxWebCache.put(cacheKey, freshRes, LuxWebCache.ttlForUrl(url)))
                .catch(() => {});
        }
        return Promise.resolve({
            data: cached.data,
            status: cached.status,
            statusText: 'OK (LuxWebCache)',
            headers: cached.headers,
            config,
            cached: true
        });
    }

    return rawApiGet(url, config)
        .then(res => {
            LuxWebCache.put(cacheKey, res, LuxWebCache.ttlForUrl(url));
            return res;
        })
        .catch(err => {
            const fallback = LuxWebCache.get(cacheKey);
            if (fallback) {
                return {
                    data: fallback.data,
                    status: fallback.status,
                    statusText: 'OK (Offline Fallback)',
                    headers: fallback.headers,
                    config,
                    cached: true
                };
            }
            throw err;
        });
};

let failedQueue = [];
let isRefreshing = false;

export function getOrCreateTelemetrySessionId() {
    try {
        if (typeof window === 'undefined') return 'web-anon';
        let sid = localStorage.getItem('luxvono_telemetry_session_id');
        if (!sid) {
            sid = `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem('luxvono_telemetry_session_id', sid);
        }
        return sid;
    } catch (_) {
        return 'web-anon';
    }
}

// Request interceptor to attach Authorization and Geolocation Country headers
api.interceptors.request.use((config) => {
    try {
        config.headers = config.headers || {};
        const token = localStorage.getItem('customer_token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        const uid = localStorage.getItem('userId') || getUserId();
        if (uid) {
            config.headers['X-User-Id'] = String(uid);
        }
        config.headers['X-Session-Id'] = getOrCreateTelemetrySessionId();
    } catch (_) {}

    try {
        if (typeof window !== 'undefined') {
            config.headers = config.headers || {};

            // 1. Check path segments first: /{country}/{language}/...
            let country = '';
            let language = '';
            const pathParts = window.location.pathname.replace(/^\/+|\/+$/g, '').split('/');
            if (pathParts.length >= 2 && /^[a-zA-Z]{2}$/.test(pathParts[0]) && /^[a-zA-Z]{2,3}(?:-[a-zA-Z]{2})?$/.test(pathParts[1])) {
                country = pathParts[0].toUpperCase();
                language = pathParts[1].toLowerCase();
            }

            // 2. Check localStorage if not in URL
            if (!country) {
                const explicitCountry = localStorage.getItem('lux_country');
                const geoStr = localStorage.getItem('luxvono_geo_context');
                if (explicitCountry) {
                    country = String(explicitCountry).toUpperCase().trim();
                } else if (geoStr) {
                    const parsed = JSON.parse(geoStr);
                    if (parsed && parsed.country) country = String(parsed.country).toUpperCase().trim();
                }
            }

            if (!language) {
                const explicitLang = localStorage.getItem('luxvono_user_explicit_language');
                if (explicitLang) language = String(explicitLang).toLowerCase().trim();
            }

            const explicitCurrency = localStorage.getItem('luxvono_user_currency');

            if (country) {
                config.headers['X-Country-Code'] = country;
                config.headers['X-User-Country'] = country;
                config.headers['X-Override-Country'] = country;
            }
            if (language) {
                config.headers['X-User-Language'] = language;
                config.headers['X-Override-Language'] = language;
                config.headers['Accept-Language'] = language;
            }
            if (explicitCurrency) {
                const normCurr = explicitCurrency.toUpperCase().trim();
                config.headers['X-User-Currency'] = normCurr;
                config.headers['X-Override-Currency'] = normCurr;
            }
        }
    } catch (_) {}

    return config;
}, (error) => Promise.reject(error));

const INTERNAL_IMAGE_HOSTS = new Set([
    'localhost',
    '127.0.0.1',
    'api-gateway',
    'ecommerce-gateway',
    'product-service',
    'ecommerce-product-service',
    'ecommerce-nginx',
    'nginx',
    'luxvono.com',
    'api.luxvono.com',
    'admin.luxvono.com'
]);

function getFlatImageValue(input) {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (Array.isArray(input)) {
        const firstValid = input.find(v => typeof v === 'string' && v.trim());
        return firstValid ? firstValid.trim() : '';
    }
    return '';
}

export function resolveStorefrontImageUrl(rawUrl) {
    const value = getFlatImageValue(rawUrl);
    if (!value) return '';

    if (value.startsWith('data:') || value.startsWith('blob:')) {
        return value;
    }

    if (value.startsWith('//')) {
        return `https:${value}`;
    }

    if (/^https?:\/\//i.test(value)) {
        try {
            const parsed = new URL(value);
            const host = String(parsed.hostname || '').toLowerCase();
            if ((INTERNAL_IMAGE_HOSTS.has(host) || host.endsWith('.local')) &&
                (parsed.pathname.startsWith('/media/') || parsed.pathname.startsWith('/uploads/'))) {
                const isAbsoluteApiBase = API_BASE && /^https?:\/\//i.test(API_BASE);
                return isAbsoluteApiBase ? `${API_BASE}${parsed.pathname}${parsed.search || ''}` : `${parsed.pathname}${parsed.search || ''}`;
            }
        } catch (_) {
            // Keep original URL if parsing fails
        }
        return value;
    }

    if (value.startsWith('/media/') || value.startsWith('/uploads/')) {
        const base = API_BASE || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'https://api.luxvono.com' : '');
        return base ? `${base}${value}` : value;
    }

    if (value.startsWith('media/') || value.startsWith('uploads/')) {
        return `${API_BASE}/${value}`;
    }

    if (value.startsWith('/api/')) {
        return `${API_BASE}${value}`;
    }

    return value.startsWith('/') ? value : `/${value}`;
}

const normalizeProductId = (value) => {
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed > 0) return parsed;
    return null;
};

const toSafeNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const deriveThumbnailFromVideoUrl = (url) => {
    const value = String(url || '').trim();
    if (!value) return '';
    const match = value.match(/^\/media\/videos\/([^/]+)\/master\.m3u8$/);
    if (!match) return '';
    return `/media/products/${match[1]}_thumb.jpg`;
};

const pickFromKeys = (source, keys = []) => {
    for (const key of keys) {
        const candidate = resolveStorefrontImageUrl(source?.[key]);
        if (candidate) return candidate;
    }
    return '';
};

const resolveVideoThumbnail = (source) => {
    const videoUrl = String(
        source?.videoUrl ||
        source?.mediaUrl ||
        source?.hlsUrl ||
        ''
    ).trim();
    if (!videoUrl) return '';

    const explicitThumbnail = pickFromKeys(source, [
        'videoThumbnailUrl',
        'videoThumbnail',
        'videoPoster',
        'poster',
        'thumbnail',
        'thumbnailUrl'
    ]);
    if (explicitThumbnail) return explicitThumbnail;

    const derived = deriveThumbnailFromVideoUrl(videoUrl);
    if (derived) return resolveStorefrontImageUrl(derived);

    return '';
};

const resolveMainImage = (source) => {
    const direct = pickFromKeys(source, [
        'primaryImageUrl',
        'mainImageUrl',
        'imageUrl',
        'image',
        'productImageUrl',
        'productImage'
    ]);
    if (direct) return direct;
    return resolveStorefrontImageUrl(source?.images);
};

const resolveThumbnailImage = (source) => pickFromKeys(source, [
    'thumbnailUrl',
    'thumbnail',
    'poster'
]);

const resolveCartPreviewMedia = (item, product) => {
    const videoThumbnail = resolveVideoThumbnail(product) || resolveVideoThumbnail(item);
    if (videoThumbnail) return videoThumbnail;

    const mainImage = resolveMainImage(product) || resolveMainImage(item);
    if (mainImage) return mainImage;

    const thumbnail = resolveThumbnailImage(product) || resolveThumbnailImage(item);
    if (thumbnail) return thumbnail;

    return '';
};

const mapRawCartItem = (item, product) => {
    const productId = normalizeProductId(item?.productId);
    if (!productId) return null;

    const qty = Math.max(1, toSafeNumber(item?.quantity, 1));
    const price = toSafeNumber(item?.price, toSafeNumber(product?.price, 0));
    const cartItemId = toSafeNumber(item?.id, 0) || null;

    return {
        id: productId,
        sku: String(item?.sku || '').trim(),
        name: String(item?.productName || product?.name || 'Product'),
        category: String(product?.categoryName || product?.category || item?.category || ''),
        price,
        qty,
        image: resolveCartPreviewMedia(item, product),
        cartItemId,
        cartItemIds: cartItemId ? [cartItemId] : []
    };
};

const mergeCartItems = (items) => {
    const merged = new Map();
    for (const row of items) {
        if (!row?.id) continue;
        const key = `${row.id}::${row.sku || ''}`;
        if (!merged.has(key)) {
            merged.set(key, {
                ...row,
                cartItemIds: Array.isArray(row.cartItemIds)
                    ? [...row.cartItemIds]
                    : (row.cartItemId ? [row.cartItemId] : [])
            });
            continue;
        }

        const current = merged.get(key);
        current.qty += Math.max(1, toSafeNumber(row.qty, 1));
        if ((!current.image || current.image.length === 0) && row.image) current.image = row.image;
        if ((!current.name || current.name === 'Product') && row.name) current.name = row.name;
        if (!current.category && row.category) current.category = row.category;
        if ((!Number.isFinite(current.price) || current.price <= 0) && Number.isFinite(row.price)) {
            current.price = row.price;
        }

        const ids = new Set([...(current.cartItemIds || []), ...(row.cartItemIds || [])].filter(Boolean));
        current.cartItemIds = [...ids];
        current.cartItemId = current.cartItemIds[0] || null;
    }

    return [...merged.values()];
};

const fetchCartProductsMap = async (rawItems) => {
    const ids = [...new Set(
        (Array.isArray(rawItems) ? rawItems : [])
            .map(item => normalizeProductId(item?.productId))
            .filter(Boolean)
    )];

    if (ids.length === 0) return new Map();

    const results = await Promise.allSettled(
        ids.map(async (id) => [id, await getProduct(id)])
    );

    const map = new Map();
    results.forEach(res => {
        if (res.status === 'fulfilled' && res.value && res.value[0]) {
            map.set(String(res.value[0]), res.value[1]);
        }
    });
    return map;
};

const buildRefreshHeaders = () => {
    const headers = {};
    try {
        const refreshToken = localStorage.getItem('customer_refresh_token');
        if (refreshToken) {
            headers['X-Refresh-Token'] = refreshToken;
        }
    } catch (_) {
        // no-op
    }
    return headers;
};

const persistAuthPayload = (payload) => {
    if (payload?.accessToken || payload?.token) {
        localStorage.setItem('customer_token', payload.accessToken || payload.token);
        window.dispatchEvent(new CustomEvent('customer_token_refreshed', { detail: payload.accessToken || payload.token }));
    }
    if (payload?.refreshToken) {
        localStorage.setItem('customer_refresh_token', payload.refreshToken);
    }
    if (payload?.user) {
        localStorage.setItem('customer_profile', JSON.stringify({
            userId: payload.user.id || payload.userId || payload.sub,
            name: payload.user.firstName || payload.user.name || payload.name || payload.sub || 'User',
            ...payload.user
        }));
    }
    return payload;
};

const clearPersistedAuth = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_refresh_token');
    localStorage.removeItem('customer_profile');
};

const extractAuthPayload = (response) => {
    const data = response?.data?.data || response?.data || {};
    return data;
};

const performRefreshRequest = async () => {
    const response = await axios.post(
        `${API_BASE}/api/v1/auth/refresh`,
        {},
        {
            withCredentials: true,
            timeout: 8000,
            headers: {
                'Content-Type': 'application/json',
                ...buildRefreshHeaders()
            }
        }
    );

    const payload = extractAuthPayload(response);
    persistAuthPayload(payload);
    return payload;
};

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

api.interceptors.response.use(
    res => {
        const method = String(res?.config?.method || 'get').toLowerCase();
        if (method === 'post' || method === 'put' || method === 'delete' || method === 'patch') {
            const url = String(res?.config?.url || '').toLowerCase();
            if (url.includes('/products') || url.includes('/categories') || url.includes('/banners') || url.includes('/videos') || url.includes('/stores')) {
                LuxWebCache.clear();
            }
        }
        return res;
    },
    async err => {
        const originalRequest = err.config;
        const requestUrl = String(originalRequest?.url || '');
        const isAuthEndpoint = requestUrl.includes('/api/v1/auth/login')
            || requestUrl.includes('/api/v1/auth/register')
            || requestUrl.includes('/api/v1/auth/request-registration-otp')
            || requestUrl.includes('/api/v1/auth/verify-registration-otp')
            || requestUrl.includes('/api/v1/auth/register-with-otp')
            || requestUrl.includes('/api/v1/auth/forgot-password')
            || requestUrl.includes('/api/v1/auth/verify-reset-password-otp')
            || requestUrl.includes('/api/v1/auth/reset-password')
            || requestUrl.includes('/api/v1/auth/refresh')
            || requestUrl.includes('/api/v1/auth/logout')
            || requestUrl.includes('/api/v1/auth/verify-2fa');

        const status = err.response?.status;
        if ((status === 401 || status === 403) && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
            const cleanUrl = String(requestUrl || '').toLowerCase();
            const isPublic = cleanUrl.includes('/api/v1/products')
                || cleanUrl.includes('/api/v1/categories')
                || cleanUrl.includes('/api/v1/feed')
                || cleanUrl.includes('/api/v1/plugins')
                || cleanUrl.includes('/api/v1/ai')
                || cleanUrl.includes('/api/v1/countries')
                || cleanUrl.includes('/api/v1/banners')
                || cleanUrl.includes('/api/v1/videos')
                || cleanUrl.includes('/api/v1/config/app')
                || (cleanUrl.includes('/api/v1/live/rooms') && !cleanUrl.includes('/join'));

            if (isPublic) {
                originalRequest._retry = true;
                clearPersistedAuth();
                if (originalRequest.headers) {
                    delete originalRequest.headers['Authorization'];
                }
                return axios(originalRequest);
            }

            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers['Authorization'] = 'Bearer ' + token;
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const payload = await performRefreshRequest();
                const newToken = payload?.accessToken || payload?.token || localStorage.getItem('customer_token');

                processQueue(null, newToken);
                if (newToken) {
                    api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
                    originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                }
                return api(originalRequest);
            } catch (refreshErr) {
                processQueue(refreshErr, null);
                clearPersistedAuth();
                return Promise.reject(refreshErr);
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(err);
    }
);

// Helper to get or create session ID
const getSessionId = () => {
    let sid = localStorage.getItem('luxvono_session_id');
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    if (!sid || !uuidRegex.test(sid)) {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            sid = crypto.randomUUID();
        } else {
            sid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        }
        localStorage.setItem('luxvono_session_id', sid);
    }
    return sid;
};

// Helper: unwrap ApiResponse
function unwrap(response) {
    const d = response.data;
    if (d?.data !== undefined) return d.data;
    if (Array.isArray(d)) return d;
    return d;
}

export function getAuthHeaders() {
    return {};
}

export function getUserId() {
    try {
        const profile = JSON.parse(localStorage.getItem('customer_profile'));
        if (profile?.userId || profile?.id) return profile.userId || profile.id;
        const token = localStorage.getItem('customer_token');
        if (token) {
            const parts = token.split('.');
            if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                return payload?.userId || payload?.id || payload?.sub || null;
            }
        }
        return null;
    } catch (e) { return null; }
}

export function getUserName() {
    try {
        const profile = JSON.parse(localStorage.getItem('customer_profile'));
        if (profile?.name || profile?.firstName || profile?.username || profile?.fullName) {
            return profile.name || profile.firstName || profile.username || profile.fullName;
        }
        const token = localStorage.getItem('customer_token');
        if (token) {
            const parts = token.split('.');
            if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                return payload?.name || payload?.firstName || payload?.sub || null;
            }
        }
        return null;
    } catch (e) { return null; }
}

export async function mergeUserCart() {
    const sid = getSessionId();
    try {
        await api.post(`/api/v1/cart/merge?sessionId=${encodeURIComponent(sid)}`);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('luxvono_cart_refresh'));
        }
    } catch (e) {
        console.warn('Failed to merge user cart:', e);
    }
}

function getBrowserTelemetry() {
    try {
        const timeZone = (typeof Intl !== 'undefined' && Intl.DateTimeFormat) 
            ? Intl.DateTimeFormat().resolvedOptions().timeZone 
            : '';
        const deviceLocale = (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage)) || 'ar';
        const countryCode = typeof localStorage !== 'undefined' ? localStorage.getItem('lux_country') : null;
        return { 
            timeZone: timeZone || undefined, 
            deviceLocale: deviceLocale || undefined,
            ...(countryCode ? { countryCode } : {})
        };
    } catch (_) {
        return {};
    }
}

export async function registerUser(payload) {
    const enriched = { ...getBrowserTelemetry(), ...payload };
    const res = await api.post('/api/v1/auth/register', enriched);
    const authPayload = unwrap(res);
    persistAuthPayload(authPayload);
    await mergeUserCart();
    return authPayload;
}

export async function unifiedInitAuth(payload) {
    const res = await api.post('/api/v1/auth/unified-init', payload);
    return unwrap(res);
}

export async function checkUsernameAvailability(username) {
    const res = await api.get(`/api/v1/auth/check-username?username=${encodeURIComponent(username)}`);
    return unwrap(res);
}

export async function requestRegistrationOtp(payload) {
    const res = await api.post('/api/v1/auth/request-registration-otp', payload);
    return unwrap(res);
}

export async function registerWithOtp(payload) {
    const enriched = { ...getBrowserTelemetry(), ...payload };
    const res = await api.post('/api/v1/auth/register-with-otp', enriched);
    const authPayload = unwrap(res);
    persistAuthPayload(authPayload);
    await mergeUserCart();
    return authPayload;
}

export async function verifyRegistrationOtp(payload) {
    const res = await api.post('/api/v1/auth/verify-registration-otp', payload);
    return unwrap(res);
}

export async function forgotPassword(payload) {
    const res = await api.post('/api/v1/auth/forgot-password', payload);
    return unwrap(res);
}

export async function verifyResetPasswordOtp(payload) {
    const res = await api.post('/api/v1/auth/verify-reset-password-otp', payload);
    return unwrap(res);
}

export async function resetPassword(payload) {
    const res = await api.post('/api/v1/auth/reset-password', payload);
    return unwrap(res);
}

export async function loginUser(payload) {
    const res = await api.post('/api/v1/auth/login', payload);
    const authPayload = unwrap(res);
    persistAuthPayload(authPayload);
    await mergeUserCart();
    return authPayload;
}

export async function googleLoginUser(idToken, extraPayload = {}) {
    const body = { idToken, ...getBrowserTelemetry(), ...extraPayload };
    const res = await api.post('/api/v1/auth/google', body);
    const authPayload = unwrap(res);
    persistAuthPayload(authPayload);
    await mergeUserCart();
    return authPayload;
}

export async function getAuthProviders() {
    try {
        const res = await api.get('/api/v1/auth/providers');
        return unwrap(res);
    } catch (e) {
        console.warn('Failed to fetch auth providers:', e);
        return {};
    }
}

export async function refreshAuthSession() {
    return performRefreshRequest();
}

export async function logoutSession(userId = null) {
    const headers = userId ? { 'X-User-Id': userId } : {};
    try {
        await api.post('/api/v1/auth/logout', {}, { headers });
    } finally {
        clearPersistedAuth();
    }
}

export async function getAuthProfile(userId = null) {
    const headers = userId ? { 'X-User-Id': userId } : {};
    const res = await api.get('/api/v1/auth/profile', { headers });
    return unwrap(res);
}

export async function updateAuthProfile(payload, userId = null) {
    const headers = userId ? { 'X-User-Id': userId } : {};
    const res = await api.put('/api/v1/auth/profile', payload, { headers });
    return unwrap(res);
}

export async function uploadAuthProfileImage(file, userId = null) {
    const headers = userId ? { 'X-User-Id': userId, 'Content-Type': 'multipart/form-data' } : { 'Content-Type': 'multipart/form-data' };
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/api/v1/auth/profile/image', formData, { headers });
    return unwrap(res);
}

// ── API Functions ──────────────────────────────────

export async function getActiveCountries() {
    const res = await api.get('/api/v1/countries/active');
    return unwrap(res) || [];
}

export async function getProducts(page = 0, size = 20, search = '', categoryId = '', hasVideo = '', vendorId = '', countryCode = '', options = {}) {
    let effectiveCountry = countryCode;
    if (!effectiveCountry && typeof window !== 'undefined') {
        try {
            const explicitCountry = localStorage.getItem('lux_country');
            const geoStr = localStorage.getItem('luxvono_geo_context');
            if (explicitCountry) {
                effectiveCountry = String(explicitCountry).toUpperCase().trim();
            } else if (geoStr) {
                const parsed = JSON.parse(geoStr);
                if (parsed && parsed.country) effectiveCountry = String(parsed.country).toUpperCase().trim();
            }
        } catch (_) {}
    }

    const params = new URLSearchParams({ page, size });
    if (search) params.append('search', search);
    if (categoryId) params.append('categoryId', categoryId);
    if (hasVideo !== '' && hasVideo !== null && hasVideo !== undefined) {
        params.append('hasVideo', hasVideo);
    }
    if (vendorId) params.append('vendorId', vendorId);
    if (effectiveCountry) params.append('countryCode', effectiveCountry);

    try {
        const res = await api.get(`/api/v1/products?${params.toString()}`, { skipCache: Boolean(options?.skipCache) });
        const data = unwrap(res);
        const content = data?.content || data || [];
        return { data: Array.isArray(content) ? content : [], totalPages: data?.totalPages || 1, currentPage: data?.number || 0 };
    } catch (e) {
        console.warn('Silent fail: products not available', e);
        return { data: [], totalPages: 1, currentPage: 0 };
    }
}

export async function getProduct(id, options = {}) {
    const res = await api.get(`/api/v1/products/${id}`, { skipCache: Boolean(options?.skipCache) });
    return unwrap(res);
}

export async function getProductReviews(id, page = 0, size = 10) {
    const res = await api.get(`/api/v1/products/${id}/reviews?page=${page}&size=${size}`);
    return unwrap(res);
}

export async function getProductReviewStats(id) {
    const res = await api.get(`/api/v1/products/${id}/reviews/stats`);
    return unwrap(res);
}

export async function submitReview(id, data) {
    const res = await api.post(`/api/v1/products/${id}/reviews`, data);
    return unwrap(res);
}

export async function createProduct(data) {
    const res = await api.post('/api/v1/admin/products', data);
    return unwrap(res);
}

export async function updateProduct(id, data) {
    const res = await api.put(`/api/v1/admin/products/${id}`, data);
    return unwrap(res);
}

export async function deleteProduct(id) {
    const res = await api.delete(`/api/v1/admin/products/${id}`);
    return unwrap(res);
}

export async function getCategories(options = {}) {
    try {
        const res = await api.get('/api/v1/categories?size=100', { skipCache: Boolean(options?.skipCache) }); // Storefront usually needs all active root categories
        const data = unwrap(res);
        const content = data?.content || data || [];
        return Array.isArray(content) ? content : [];
    } catch (e) {
        console.warn('Silent fail: categories not available');
        return [];
    }
}

export async function getVideos(countryCode = '', options = {}) {
    try {
        let effectiveCountry = countryCode;
        if (!effectiveCountry && typeof window !== 'undefined') {
            const explicitCountry = localStorage.getItem('lux_country');
            const geoStr = localStorage.getItem('luxvono_geo_context');
            if (explicitCountry) {
                effectiveCountry = String(explicitCountry).toUpperCase().trim();
            } else if (geoStr) {
                try {
                    const parsed = JSON.parse(geoStr);
                    if (parsed && parsed.country) effectiveCountry = String(parsed.country).toUpperCase().trim();
                } catch (_) {}
            }
        }

        const params = new URLSearchParams();
        if (effectiveCountry) params.append('countryCode', effectiveCountry);
        if (options.sort) params.append('sort', options.sort);
        if (options.categoryId) params.append('categoryId', options.categoryId);

        // Include client-side category affinities for guest personalization
        const savedAffinities = options.affinities || (typeof window !== 'undefined' ? localStorage.getItem('luxvono_category_affinities') : null);
        if (savedAffinities) params.append('affinities', savedAffinities);

        const qs = params.toString() ? `?${params.toString()}` : '';
        const res = await api.get(`/api/v1/feed${qs}`, { skipCache: Boolean(options?.skipCache) });
        return unwrap(res);
    } catch (e) {
        console.warn('Silent fail: feed not available');
        return [];
    }
}

export async function likeStorefrontVideo(videoId) {
    try {
        const userId = localStorage.getItem('userId');
        const userName = localStorage.getItem('userName');
        const headers = {};
        if (userId) headers['X-User-Id'] = userId;
        if (userName) headers['X-User-Name'] = userName;
        const res = await api.post(`/api/v1/videos/${videoId}/like`, {}, { headers });
        return unwrap(res);
    } catch (e) {
        console.warn('Silent fail: like video error');
        return false;
    }
}

export async function viewStorefrontVideo(videoId) {
    try {
        const userId = localStorage.getItem('userId');
        const userName = localStorage.getItem('userName');
        const headers = {};
        if (userId) headers['X-User-Id'] = userId;
        if (userName) headers['X-User-Name'] = userName;
        const res = await api.post(`/api/v1/videos/${videoId}/view`, {}, { headers });
        return unwrap(res);
    } catch (e) {
        console.warn('Silent fail: record view video error');
        return false;
    }
}

export async function getBanners(location, options = {}) {
    try {
        const url = location ? `/api/v1/banners?location=${encodeURIComponent(location)}` : '/api/v1/banners';
        const res = await api.get(url, { skipCache: Boolean(options?.skipCache) });
        return unwrap(res);
    } catch (e) {
        console.warn('Silent fail: banners not available', e);
        return [];
    }
}

export async function registerBannerClick(id) {
    await api.post(`/api/v1/banners/${id}/click`);
}

export async function getPromotions() {
    const res = await api.get('/api/v1/orders/promotions');
    return unwrap(res);
}

export async function validateCoupon(code, orderAmount) {
    const res = await api.get('/api/v1/orders/promotions/validate', {
        params: { code, orderAmount }
    });
    return unwrap(res);
}

// ── Cart Functions ─────────────────────────────────

export async function getCart() {
    const sid = getSessionId();
    try {
        const res = await api.get('/api/v1/cart', { headers: { 'X-Session-Id': sid } });
        const cart = unwrap(res);
        const rawItems = Array.isArray(cart?.items) ? cart.items : [];
        if (rawItems.length === 0) return [];

        const productsMap = await fetchCartProductsMap(rawItems);

        const normalizedItems = rawItems
            .map(item => {
                const product = productsMap.get(normalizeProductId(item?.productId));
                return mapRawCartItem(item, product);
            })
            .filter(Boolean);

        return mergeCartItems(normalizedItems);
    } catch (e) {
        throw e;
    }
}

export async function addToCart(product, qty = 1) {
    const sid = getSessionId();
    await api.post('/api/v1/cart/add', {
        productId: product.id,
        quantity: qty
    }, { headers: { 'X-Session-Id': sid } });
    return getCart();
}

export async function removeFromCart(itemId) {
    const sid = getSessionId();
    await api.delete(`/api/v1/cart/items/${itemId}`, { headers: { 'X-Session-Id': sid } });
    return getCart();
}

export async function removeFromCartItems(itemIds = []) {
    const sid = getSessionId();
    const uniqueIds = [...new Set(
        (Array.isArray(itemIds) ? itemIds : [])
            .map(id => toSafeNumber(id, 0))
            .filter(id => Number.isInteger(id) && id > 0)
    )];

    for (const itemId of uniqueIds) {
        await api.delete(`/api/v1/cart/items/${itemId}`, { headers: { 'X-Session-Id': sid } });
    }

    return getCart();
}

// ── Checkout Functions ─────────────────────────────

export async function createOrder(payload) {
    const res = await api.post('/api/v1/orders', payload);
    return unwrap(res);
}

export async function getAvailablePaymentGateways(totalAmount = 0, currency = 'SAR') {
    try {
        const res = await api.get('/api/v1/payments/gateways/available', {
            params: { totalAmount, currency }
        });
        return unwrap(res);
    } catch (e) {
        console.warn('Failed to load dynamic regional gateways:', e);
        return null;
    }
}

export async function createRegionalPaymentIntent(payload) {
    const res = await api.post('/api/v1/payments/regional-intent', payload);
    return unwrap(res);
}

export async function confirmPayment(paymentId, providerPaymentId) {
    const res = await api.post(`/api/v1/payments/${paymentId}/confirm`, {
        providerPaymentId
    });
    return unwrap(res);
}

export async function getOrdersByUser(userId) {
    if (!userId) return [];
    try {
        const res = await api.get(`/api/v1/orders`, { headers: { 'X-User-Id': userId } });
        const payload = unwrap(res);
        const list = Array.isArray(payload) ? payload : (payload?.content || []);
        return list
            .map(order => ({
                id: order.id,
                orderNumber: order.orderNumber || `ORD-${order.id}`,
                status: order.status || 'PENDING',
                totalAmount: Number(order.totalAmount || order.total || 0),
                itemCount: Array.isArray(order.items) ? order.items.length : Number(order.itemCount || 0),
                createdAt: order.createdAt || null,
                courierId: order.courierId || null,
                driverName: order.driverName || null,
                driverPhone: order.driverPhone || null,
                carrierName: order.carrierName || null,
                carrierCode: order.carrierCode || null,
                trackingNumber: order.trackingNumber || null,
                shippingAddress: order.shippingAddress || null
            }))
            .sort((a, b) => {
                const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
                const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
                return bTime - aTime;
            });
    } catch (error) {
        console.warn('Failed to load order history:', error?.message || error);
        return [];
    }
}

// ── Chat / AI Assistant ───────────────────────────
export async function startSupportSession(userId, orderId = null, sellerId = null, productId = null, variantId = null) {
    let url = `/api/v1/chat/sessions?customerId=${encodeURIComponent(userId)}`;
    if (sellerId) url += `&sellerId=${encodeURIComponent(sellerId)}`;
    if (orderId) url += `&orderId=${encodeURIComponent(orderId)}`;
    if (productId) url += `&productId=${encodeURIComponent(productId)}`;
    if (variantId) url += `&variantId=${encodeURIComponent(variantId)}`;
    const res = await api.post(url);
    return unwrap(res);
}

export async function getCustomerChatThreads() {
    const res = await api.get('/api/v1/chat/customer/threads');
    return unwrap(res);
}

export async function getSupportConversation(userId) {
    const res = await api.get(`/api/v1/chat/conversation/${encodeURIComponent(userId)}`);
    return unwrap(res);
}

export async function sendSupportMessage(payload) {
    const res = await api.post('/api/v1/chat/message', payload);
    return unwrap(res);
}

export async function uploadChatMedia(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/api/v1/chat/upload', formData);
    return unwrap(res);
}

export async function getSellerChatThreads(sellerId) {
    const res = await api.get(`/api/v1/chat/seller/threads?sellerId=${encodeURIComponent(sellerId)}`);
    return unwrap(res);
}

export async function getSessionChatMessages(sessionId) {
    const res = await api.get(`/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages`);
    return unwrap(res);
}

export async function sendChatMessage(payload) {
    const res = await api.post('/api/v1/chat/message', payload);
    return unwrap(res);
}

export async function markChatSessionRead(sessionId, readerUserId) {
    const res = await api.post(`/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/mark-read?readerUserId=${encodeURIComponent(readerUserId)}`);
    return unwrap(res);
}

// ── Social Commerce & C2C Endpoints ──────────────────
export async function getVerifiedSellers(params = {}) {
    const res = await api.get('/api/v1/sellers', { params });
    return unwrap(res);
}

export async function searchCustomerProfiles(query, page = 0, size = 15) {
    const res = await api.get('/api/v1/users/search', { params: { query, page, size } });
    return unwrap(res);
}

export async function getC2cChatThreads() {
    const res = await api.get('/api/v1/chat/c2c/threads');
    return unwrap(res);
}

export async function getOrCreateC2cSession(recipientUserId) {
    const res = await api.post(`/api/v1/chat/c2c/sessions?recipientUserId=${encodeURIComponent(recipientUserId)}`);
    return unwrap(res);
}

export async function blockUser(blockedUserId, reason = 'User blocked in chat', blockerId = null) {
    let url = `/api/v1/chat/block-user?blockedUserId=${encodeURIComponent(blockedUserId)}&blockedId=${encodeURIComponent(blockedUserId)}&reason=${encodeURIComponent(reason)}`;
    if (blockerId) url += `&blockerId=${encodeURIComponent(blockerId)}`;
    const res = await api.post(url, {
        blockerId,
        blockedId: blockedUserId,
        blockedUserId,
        reason
    });
    return unwrap(res);
}

export async function unblockUser(blockedUserId, unblockerId = null) {
    let url = `/api/v1/chat/unblock-user?blockedUserId=${encodeURIComponent(blockedUserId)}&unblockedId=${encodeURIComponent(blockedUserId)}&blockedId=${encodeURIComponent(blockedUserId)}`;
    if (unblockerId) url += `&unblockerId=${encodeURIComponent(unblockerId)}&blockerId=${encodeURIComponent(unblockerId)}`;
    const res = await api.post(url, {
        unblockerId,
        blockerId: unblockerId,
        unblockedId: blockedUserId,
        blockedId: blockedUserId,
        blockedUserId
    });
    return unwrap(res);
}

export async function getChatBlockStatus(userId1, userId2) {
    const res = await api.get(`/api/v1/chat/block-status?userId1=${encodeURIComponent(userId1)}&userId2=${encodeURIComponent(userId2)}`);
    return unwrap(res);
}

export async function getBlockedUsers(page = 0, size = 20) {
    const res = await api.get('/api/v1/chat/blocked-users', { params: { page, size } });
    return unwrap(res);
}

export async function createC2cOrder(orderData) {
    const res = await api.post('/api/v1/orders', orderData);
    return unwrap(res);
}

export async function askAiAssistant(message, userId = null, conversationHistory = [], lang = null) {
    let activeLang = lang;
    if (!activeLang && typeof window !== 'undefined') {
        try {
            const geo = JSON.parse(localStorage.getItem('luxvono_geo_context') || '{}');
            activeLang = geo?.language || localStorage.getItem('luxvono_user_explicit_language') || document.documentElement.lang || 'ar';
        } catch (e) {
            activeLang = 'ar';
        }
    }
    const res = await api.post('/api/v1/ai/chat', { 
        message, 
        userId, 
        conversationHistory, 
        lang: activeLang || 'ar',
        locale: activeLang || 'ar'
    });
    return unwrap(res);
}

export async function getPersonalizedRecommendations(limit = 10) {
    const res = await api.get(`/api/v1/recommendations/personalized?limit=${limit}`, { skipCache: true });
    return unwrap(res) || [];
}

export async function getServerRecentlyViewed(limit = 10) {
    const res = await api.get(`/api/v1/recommendations/recently-viewed?limit=${limit}`, { skipCache: true });
    return unwrap(res) || [];
}

export function trackCustomerEvent(eventType, { productId = null, categoryId = null, durationMs = null, metadata = null } = {}) {
    try {
        const uid = Number(localStorage.getItem('userId') || getUserId() || 0) || null;
        const sessionId = getOrCreateTelemetrySessionId();
        const eventPayload = [{
            eventType,
            userId: uid,
            sessionId,
            productId: productId ? Number(productId) : null,
            categoryId: categoryId ? Number(categoryId) : null,
            durationMs: durationMs ? Number(durationMs) : null,
            metadata: metadata || {},
            timestamp: new Date().toISOString()
        }];
        api.post('/api/v1/analytics/events', eventPayload).catch(() => {});
    } catch (_) {}
}

export async function getSimilarProducts(productId, limit = 6) {
    const res = await api.get(`/api/v1/recommendations/similar/${productId}?limit=${limit}`);
    return unwrap(res) || [];
}

export async function getUpsellProducts(productId, limit = 4) {
    const res = await api.get(`/api/v1/recommendations/upsell/${productId}?limit=${limit}`);
    return unwrap(res) || [];
}

export async function getComplementaryProducts(productId, limit = 4) {
    const res = await api.get(`/api/v1/recommendations/complements/${productId}?limit=${limit}`);
    return unwrap(res) || [];
}

export async function submitContactMessage(payload) {
    const res = await api.post('/api/v1/plugins/contact', payload);
    return unwrap(res);
}

// ── Agent PWA APIs ──────────────────────────────
export async function verifyAgentToken(token) {
    const res = await api.get(`/api/v1/agent-portal/verify?token=${encodeURIComponent(token)}`);
    return unwrap(res);
}

export async function getAgentSessions(token) {
    const res = await api.get(`/api/v1/agent-portal/sessions?token=${encodeURIComponent(token)}`);
    return unwrap(res) || [];
}

export async function getAgentChatHistory(sessionId, token) {
    const res = await api.get(`/api/v1/agent-portal/sessions/${sessionId}/messages?token=${encodeURIComponent(token)}`);
    return unwrap(res) || [];
}

export async function claimAgentSession(sessionId, token) {
    const res = await api.post(`/api/v1/agent-portal/sessions/${sessionId}/claim?token=${encodeURIComponent(token)}`);
    return unwrap(res);
}

export async function sendAgentMessage(token, payload) {
    const res = await api.post(`/api/v1/agent-portal/messages?token=${encodeURIComponent(token)}`, payload);
    return unwrap(res);
}

// ── Live Rooms ─────────────────────────────────

export const liveAPI = {
    getActiveRooms: (country) => {
        const params = country ? { country } : {};
        return api.get('/api/v1/live/rooms', { params }).then(res => unwrap(res));
    },
    getParticipants: (roomId) => api.get(`/api/v1/live/rooms/${roomId}/participants`).then(res => unwrap(res)),
    createRoom: (payload) => api.post('/api/v1/live/rooms', payload).then(res => unwrap(res)),
    closeRoom: (roomId) => api.post(`/api/v1/live/rooms/${roomId}/close`).then(res => unwrap(res)).catch(() => api.delete(`/api/v1/live/rooms/${roomId}`)),
    joinRoom: (roomId, userName, isHostMode = false, secretCode = '') => api.post(`/api/v1/live/rooms/${roomId}/join`, null, {
        headers: { 'X-User-Name': encodeURIComponent(userName) },
        params: { isHostMode, secretCode }
    }).then(res => unwrap(res)),
    leaveRoom: (roomId) => api.delete(`/api/v1/live/rooms/${roomId}/leave`),
    subscribeReminder: (roomId, sellerId) => {
        try {
            const key = 'luxvono_live_reminders';
            const existing = JSON.parse(localStorage.getItem(key) || '[]');
            if (!existing.includes(String(roomId))) {
                existing.push(String(roomId));
                localStorage.setItem(key, JSON.stringify(existing));
            }
        } catch (_) {}
        return api.post(`/api/v1/live/rooms/${roomId}/remind`, { sellerId }).then(res => unwrap(res)).catch(() => ({ success: true }));
    }
};

export default api;

export const verifyEmail = async (email, token) => {
    const res = await api.post('/api/v1/auth/verify-email', { email, token });
    return res.data;
};

export const resendVerificationEmail = async (email) => {
    const res = await api.post('/api/v1/auth/resend-verification', { email });
    return res.data;
};

// ── Vendor & Supplier Applications ─────────────────
export async function submitVendorApplication(data) {
    const res = await api.post('/api/v1/vendor/apply', data);
    return unwrap(res);
}

export async function getVendorApplicationStatus() {
    try {
        const res = await api.get('/api/v1/vendor/application/status');
        const unwrapped = unwrap(res);
        if (!unwrapped) return null;
        if (typeof unwrapped === 'object') {
            unwrapped.data = unwrapped;
        }
        return unwrapped;
    } catch (_) {
        return null;
    }
}

export async function uploadVendorDocument(file, docType = 'doc') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('docType', docType);
    const res = await api.post('/api/v1/vendor/upload-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return unwrap(res);
}

export async function getVendorProducts() {
    const userId = localStorage.getItem('userId');
    const res = await api.get(`/api/v1/products?vendorId=${userId || ''}&size=50`);
    return unwrap(res);
}

export async function uploadMedia(files, onUploadProgress, mode = 'AUTO') {
    const formData = new FormData();
    Array.from(files).forEach((file, idx) => {
        const isVideo = file.type?.startsWith('video/') || /\.(mp4|mov|webm|m4v|mkv|avi|quicktime)$/i.test(file.name || '');
        const extension = file.name && file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : (isVideo ? '.mp4' : '.jpg');
        const fallbackName = `${isVideo ? 'video' : 'image'}_${Date.now()}_${idx}${extension}`;
        formData.append('files', file, file.name || fallbackName);
    });
    const effectiveMode = mode || 'AUTO';
    const res = await api.post(`/api/v1/admin/media/upload?mode=${effectiveMode}`, formData, {
        onUploadProgress,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 minutes for large 4K video uploads
    });
    return unwrap(res);
}

export async function getJobStatus(jobId) {
    const res = await api.get(`/api/v1/admin/media/jobs/${jobId}`);
    return unwrap(res);
}

// ── Real Multi-Carrier Logistics Tracking ───────────
export async function trackShipment(trackingNumber) {
    const clean = String(trackingNumber || '').trim();
    if (!clean) return { found: false };
    const res = await api.get(`/api/v1/logistics/track/${encodeURIComponent(clean)}`);
    return unwrap(res);
}

// ── Customer Friendship & C2C Calling Permissions ───────────
function normalizeFriendshipData(data) {
    if (!data || typeof data !== 'object') {
        return { isFriend: false, friend: false, canCall: false, status: 'NONE' };
    }
    const isFriend = Boolean(data.isFriend || data.friend || data.canCall || data.status === 'ACCEPTED');
    return {
        ...data,
        isFriend,
        friend: isFriend,
        canCall: isFriend || Boolean(data.canCall),
        status: isFriend ? 'ACCEPTED' : (data.status || 'NONE')
    };
}

export async function getFriendshipStatus(peerId) {
    if (!peerId) return { isFriend: false, friend: false, canCall: false, status: 'NONE' };
    try {
        const res = await api.get(`/api/v1/chat/friends/status?peerId=${encodeURIComponent(peerId)}`);
        return normalizeFriendshipData(unwrap(res));
    } catch (_) {
        return { isFriend: false, friend: false, canCall: false, status: 'NONE' };
    }
}

export async function sendFriendRequest(peerId) {
    if (!peerId) throw new Error('peerId is required');
    const res = await api.post(`/api/v1/chat/friends/request?peerId=${encodeURIComponent(peerId)}`);
    return normalizeFriendshipData(unwrap(res));
}

export async function acceptFriendRequest(peerId) {
    if (!peerId) throw new Error('peerId is required');
    const res = await api.post(`/api/v1/chat/friends/accept?peerId=${encodeURIComponent(peerId)}`);
    return normalizeFriendshipData(unwrap(res));
}

export async function declineFriendRequest(peerId) {
    if (!peerId) throw new Error('peerId is required');
    const res = await api.post(`/api/v1/chat/friends/decline?peerId=${encodeURIComponent(peerId)}`);
    return normalizeFriendshipData(unwrap(res));
}

export async function removeFriend(peerId) {
    if (!peerId) throw new Error('peerId is required');
    const res = await api.post(`/api/v1/chat/friends/remove?peerId=${encodeURIComponent(peerId)}`);
    return normalizeFriendshipData(unwrap(res));
}

export async function getFriendsList() {
    const res = await api.get('/api/v1/chat/friends/list');
    return unwrap(res);
}

export async function deleteC2cChatThread(sessionId) {
    if (!sessionId) throw new Error('sessionId is required');
    const res = await api.delete(`/api/v1/chat/c2c/threads/${encodeURIComponent(sessionId)}`);
    return unwrap(res);
}


